/** 관리자 «유료 이벤트 이용권» 상품 등록. 운영 저장소는 Supabase(DB), localStorage는 이전·폴백. */

import { planEndIsoFromStartMonthAndDurationMonths } from './academicYearFebruaryEnd';

const MAX_ROWS = 400;

export type AdminPaidEventProductSaleStatus = 'active' | 'closed';

export interface AdminPaidEventPassProduct {
  id: string;
  issuedAt: string;
  eventName: string;
  /** 고정 결제 금액(원) */
  flatAmountWon: number;
  paidPlanStartMonth: string;
  durationMonths: number;
  planEndIso: string;
  /** 구매 가능 인원 상한(슬롯) */
  activeUserCount: number;
  /** 누적 구매 수(데모: 이 브라우저 localStorage 기준) */
  soldCount: number;
  saleStatus: AdminPaidEventProductSaleStatus;
}

function storageKey(workspaceId: string): string {
  return `cp_admin_paid_event_product_v1:${workspaceId.trim()}`;
}

export function newAdminPaidEventProductId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `pep_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

function parseRows(raw: string | null): unknown[] {
  if (!raw) return [];
  try {
    const j = JSON.parse(raw) as unknown;
    return Array.isArray(j) ? j : [];
  } catch {
    return [];
  }
}

function migrateRow(o: Record<string, unknown>): AdminPaidEventPassProduct | null {
  if (typeof o.id !== 'string' || typeof o.issuedAt !== 'string') return null;
  const eventName =
    typeof o.eventName === 'string' && o.eventName.trim() ? o.eventName.trim() : '(이름 없음)';
  let paidPlanStartMonth = typeof o.paidPlanStartMonth === 'string' ? o.paidPlanStartMonth.trim() : '';
  if (!/^\d{4}-\d{2}$/.test(paidPlanStartMonth)) {
    paidPlanStartMonth = new Date().toISOString().slice(0, 7);
  }
  const durationRaw = o.durationMonths;
  const durationMonths =
    typeof durationRaw === 'number' && Number.isFinite(durationRaw) && durationRaw > 0
      ? Math.min(36, Math.floor(durationRaw))
      : 1;
  const recomputed = planEndIsoFromStartMonthAndDurationMonths(paidPlanStartMonth, durationMonths);
  let planEndIso =
    typeof o.planEndIso === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(o.planEndIso.trim())
      ? o.planEndIso.trim()
      : '';
  if (!planEndIso && recomputed) planEndIso = recomputed;
  if (!planEndIso) {
    planEndIso = planEndIsoFromStartMonthAndDurationMonths(paidPlanStartMonth, 1) || '';
  }
  const flatRaw = o.flatAmountWon;
  const flatAmountWon =
    typeof flatRaw === 'number' && Number.isFinite(flatRaw) && flatRaw >= 0
      ? Math.min(9_999_999, Math.floor(flatRaw))
      : 3000;
  const activeRaw = o.activeUserCount;
  const activeUserCount =
    typeof activeRaw === 'number' && Number.isFinite(activeRaw) && activeRaw > 0
      ? Math.min(999, Math.floor(activeRaw))
      : 1;
  const soldRaw = o.soldCount;
  const soldCount =
    typeof soldRaw === 'number' && Number.isFinite(soldRaw) && soldRaw >= 0
      ? Math.min(activeUserCount, Math.floor(soldRaw))
      : 0;
  const saleStatus: AdminPaidEventProductSaleStatus = o.saleStatus === 'closed' ? 'closed' : 'active';
  return {
    id: o.id,
    issuedAt: o.issuedAt,
    eventName,
    flatAmountWon,
    paidPlanStartMonth,
    durationMonths,
    planEndIso,
    activeUserCount,
    soldCount,
    saleStatus,
  };
}

export function loadPaidEventPassProducts(workspaceId: string): AdminPaidEventPassProduct[] {
  if (typeof window === 'undefined') return [];
  return parseRows(window.localStorage.getItem(storageKey(workspaceId)))
    .map((x) => (x && typeof x === 'object' ? migrateRow(x as Record<string, unknown>) : null))
    .filter((x): x is AdminPaidEventPassProduct => x !== null)
    .slice(0, MAX_ROWS);
}

export function savePaidEventPassProducts(
  workspaceId: string,
  rows: AdminPaidEventPassProduct[],
): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(storageKey(workspaceId), JSON.stringify(rows.slice(0, MAX_ROWS)));
}

export function prependPaidEventPassProduct(
  workspaceId: string,
  row: AdminPaidEventPassProduct,
): void {
  savePaidEventPassProducts(workspaceId, [row, ...loadPaidEventPassProducts(workspaceId)]);
}

export function deletePaidEventPassProduct(workspaceId: string, id: string): void {
  savePaidEventPassProducts(
    workspaceId,
    loadPaidEventPassProducts(workspaceId).filter((r) => r.id !== id),
  );
}

export function patchPaidEventPassProduct(
  workspaceId: string,
  id: string,
  patch: Partial<Omit<AdminPaidEventPassProduct, 'id'>>,
): void {
  savePaidEventPassProducts(
    workspaceId,
    loadPaidEventPassProducts(workspaceId).map((r) => {
      if (r.id !== id) return r;
      const merged = { ...r, ...patch };
      if (merged.soldCount > merged.activeUserCount) {
        merged.soldCount = merged.activeUserCount;
      }
      return merged;
    }),
  );
}

/** 학교 스코프에만 있던 데모 데이터를 플랫폼 스코프로 한 번 이전 */
export function migratePaidEventPassProductsIfEmpty(
  targetWorkspaceId: string,
  fromWorkspaceId: string,
): void {
  const target = targetWorkspaceId.trim();
  const from = fromWorkspaceId.trim();
  if (!target || !from || target === from) return;
  if (loadPaidEventPassProducts(target).length > 0) return;
  const legacy = loadPaidEventPassProducts(from);
  if (legacy.length === 0) return;
  savePaidEventPassProducts(target, legacy);
}

/** 이용권 카드용: 판매중(active) 상품만, 여러 스코프를 id 기준 합침 */
export function loadActivePaidEventPassProductsForSale(
  ...workspaceIds: string[]
): AdminPaidEventPassProduct[] {
  const seen = new Set<string>();
  const out: AdminPaidEventPassProduct[] = [];
  for (const wid of workspaceIds) {
    if (!wid?.trim()) continue;
    for (const p of loadPaidEventPassProducts(wid)) {
      if (p.saleStatus !== 'active' || seen.has(p.id)) continue;
      seen.add(p.id);
      out.push(p);
    }
  }
  return out;
}

export function isPaidEventProductSoldOut(product: AdminPaidEventPassProduct): boolean {
  return product.soldCount >= product.activeUserCount;
}

export function remainingPaidEventProductSlots(product: AdminPaidEventPassProduct): number {
  return Math.max(0, product.activeUserCount - product.soldCount);
}

/** 구매 성공 시 슬롯 1 소진. 한도 초과·마감이면 실패 */
export function tryIncrementPaidEventProductSoldCount(
  workspaceId: string,
  id: string,
): { ok: true; product: AdminPaidEventPassProduct } | { ok: false; reason: 'not_found' | 'sold_out' | 'closed' } {
  const rows = loadPaidEventPassProducts(workspaceId);
  const idx = rows.findIndex((r) => r.id === id);
  if (idx < 0) return { ok: false, reason: 'not_found' };
  const row = rows[idx]!;
  if (row.saleStatus === 'closed') return { ok: false, reason: 'closed' };
  if (isPaidEventProductSoldOut(row)) return { ok: false, reason: 'sold_out' };
  const next: AdminPaidEventPassProduct = { ...row, soldCount: row.soldCount + 1 };
  const nextRows = [...rows];
  nextRows[idx] = next;
  savePaidEventPassProducts(workspaceId, nextRows);
  return { ok: true, product: next };
}

/** 상품이 있는 첫 스코프에서 슬롯 소진 시도 */
export function tryIncrementPaidEventProductSoldCountInScopes(
  productId: string,
  ...workspaceIds: string[]
): { ok: true; product: AdminPaidEventPassProduct } | { ok: false; reason: 'not_found' | 'sold_out' | 'closed' } {
  let lastFail: { ok: false; reason: 'not_found' | 'sold_out' | 'closed' } = {
    ok: false,
    reason: 'not_found',
  };
  for (const wid of workspaceIds) {
    if (!wid?.trim()) continue;
    const result = tryIncrementPaidEventProductSoldCount(wid, productId);
    if (result.ok) return result;
    if (result.reason !== 'not_found') return result;
    lastFail = result;
  }
  return lastFail;
}
